import { FuelType } from '@/entities/cars/fuel-type.enum';
import { InspectionShotKey } from '@carvia/ros-client-types';

/** Overlay drawing for one Inspection shot, in a 1600×900 landscape frame. */
export type ShotOverlay = {
  /** Main silhouette, drawn bold with glow. */
  outline: string[];
  /** Secondary detail lines, drawn thin. */
  details: string[];
  /** Circles as [cx, cy, r], drawn like details. */
  circles?: [number, number, number][];
  /** Mirror horizontally (right side of the car). */
  mirror?: boolean;
  /** Enlarges the drawing: point `from` (drawing coords) lands on `to` (frame coords). */
  zoom?: { scale: number; from: [number, number]; to: [number, number] };
  /** Ground or reference line, drawn dashed. */
  ground?: string;
};

/** Where to stand: positions on a top-down car (120×200, front at the top). */
export type ShotLocator = {
  camera: [number, number];
  target: [number, number];
};

export type ShotGuide = {
  overlay: ShotOverlay;
  locator: ShotLocator;
};

/*
 * Exterior drawings follow the proportions of a Porsche Cayenne (4918 × 1983 × 1696 mm,
 * wheelbase 2895 mm). They were generated from millimetre coordinates, so wheelbase, overhangs,
 * wheel size, roof line and track stay true to a real mid-size SUV.
 */

/** Body shell and tyres seen straight from the front or rear. */
const FACE_OUTLINE = [
  'M 800 148 Q 982 148 1036 159 Q 1051 163 1056 178 L 1119 334 Q 1123 345 1142 349 Q 1170 353 1174 380 Q 1178 448 1177 562 L 1176 680 Q 1176 692 1165 693 L 1068 693 Q 1062 693 1058 699 L 800 702 L 542 699 Q 538 693 532 693 L 435 693 Q 424 692 424 680 L 423 562 Q 422 448 426 380 Q 430 353 458 349 Q 477 345 481 334 L 544 178 Q 549 163 564 159 Q 618 148 800 148 Z',
  'M 1068 693 L 1068 780 Q 1068 790 1078 790 L 1166 790 Q 1176 790 1176 780 L 1176 693',
  'M 424 693 L 424 780 Q 424 790 434 790 L 522 790 Q 532 790 532 780 L 532 693',
];

const FACE_GROUND = 'M 230 792 L 1370 792';

const FRONT: ShotOverlay = {
  outline: FACE_OUTLINE,
  details: [
    'M 800 174 Q 967 174 1028 182 Q 1039 184 1043 195 L 1094 328 Q 1098 340 1083 341 Q 941 346 800 346 Q 659 346 517 341 Q 502 340 506 328 L 557 195 Q 561 184 572 182 Q 633 174 800 174',
    'M 1123 338 L 1203 324 Q 1218 321 1217 304 Q 1216 285 1195 285 L 1131 296',
    'M 477 338 L 397 324 Q 382 321 383 304 Q 384 285 405 285 L 469 296',
    'M 511 362 Q 800 387 1089 362',
    'M 960 410 L 1153 387 Q 1167 399 1161 425 L 971 440 Q 956 433 960 410 Z',
    'M 640 410 L 447 387 Q 433 399 439 425 L 629 440 Q 644 433 640 410 Z',
    'M 998 416 L 1134 402',
    'M 602 416 L 466 402',
    'M 800 478 L 960 478 Q 979 479 975 497 L 960 611 Q 956 627 937 627 L 800 627 L 663 627 Q 644 627 640 611 L 625 497 Q 621 479 640 478 L 800 478',
    'M 1013 486 L 1142 471 L 1134 619 L 1028 615 Z',
    'M 587 486 L 458 471 L 466 619 L 572 615 Z',
    'M 701 501 L 899 501 L 899 543 L 701 543 Z',
    'M 534 661 Q 800 652 1066 661',
  ],
  ground: FACE_GROUND,
};

const REAR: ShotOverlay = {
  outline: FACE_OUTLINE,
  details: [
    'M 800 197 Q 952 197 1013 203 Q 1024 205 1028 216 L 1066 315 Q 1069 326 1055 327 Q 925 332 800 332 Q 675 332 545 327 Q 531 326 534 315 L 572 216 Q 576 205 587 203 Q 648 197 800 197',
    'M 564 177 Q 800 170 1036 177',
    'M 1123 338 L 1203 324 Q 1218 321 1217 304 Q 1216 285 1195 285 L 1131 296',
    'M 477 338 L 397 324 Q 382 321 383 304 Q 384 285 405 285 L 469 296',
    'M 534 372 L 1066 372 L 1066 383 L 534 383 Z',
    'M 1165 362 L 1066 368 L 1066 395 L 1163 399 Z',
    'M 435 362 L 534 368 L 534 395 L 437 399 Z',
    'M 800 340 L 1074 340 L 1081 501 Q 1081 516 1066 516 L 800 516 L 534 516 Q 519 516 519 501 L 526 340 L 800 340',
    'M 697 425 L 903 425 L 903 471 L 697 471 Z',
    'M 426 547 Q 800 535 1174 547',
    'M 564 676 Q 800 668 1036 676',
    'M 1013 661 L 1077 661 L 1077 682 L 1013 682 Z',
    'M 587 661 L 523 661 L 523 682 L 587 682 Z',
    'M 1104 592 L 1161 590',
    'M 496 592 L 439 590',
  ],
  ground: FACE_GROUND,
};

/** Profile with the nose to the left; the right side mirrors it. */
const SIDE: ShotOverlay = {
  outline: [
    'M 174 669 Q 155 663 153 621 L 152 558 Q 152 521 169 500 Q 182 488 219 484 Q 361 461 494 444 Q 586 378 660 325 Q 684 309 731 307 Q 1022 301 1260 332 L 1286 337 Q 1296 340 1291 349 Q 1355 404 1416 448 Q 1444 464 1448 489 L 1449 600 Q 1449 661 1424 669 L 1289 682 A 116 116 0 1 0 1065 682 L 523 682 A 116 116 0 1 0 300 682 L 174 669 Z',
  ],
  details: [
    'M 536 440 Q 615 383 668 341 Q 689 327 731 326 L 842 326 L 842 437 Z',
    'M 864 326 L 1101 327 L 1112 429 L 864 436 Z',
    'M 1133 329 L 1207 336 Q 1231 339 1242 352 L 1286 407 Q 1273 419 1252 420 L 1133 428 Z',
    'M 213 494 Q 494 473 837 465 Q 1154 457 1445 464',
    'M 536 650 L 1053 650',
    'M 531 444 Q 539 563 541 611 Q 544 637 562 648',
    'M 853 326 L 854 661',
    'M 1123 429 Q 1117 505 1086 537 Q 1067 552 1057 563',
    'M 731 461 L 790 460',
    'M 1020 457 L 1075 456',
    'M 158 521 Q 229 497 319 484 L 324 501 Q 237 513 161 539 Z',
    'M 1446 464 L 1334 444 L 1339 430 L 1444 447 Z',
    'M 539 436 Q 502 433 494 418 Q 491 401 515 399 L 546 407',
    'M 154 589 Q 216 600 298 595',
    'M 1449 579 Q 1392 587 1323 576',
    'M 407 668 L 406 716',
    'M 416 668 L 418 716',
    'M 428 660 L 473 676',
    'M 431 651 L 477 665',
    'M 426 638 L 455 599',
    'M 419 632 L 446 593',
    'M 405 632 L 377 593',
    'M 397 638 L 368 599',
    'M 393 651 L 347 665',
    'M 396 660 L 350 676',
    'M 1172 668 L 1171 716',
    'M 1182 668 L 1183 716',
    'M 1193 660 L 1238 676',
    'M 1196 651 L 1242 665',
    'M 1192 638 L 1221 599',
    'M 1184 632 L 1211 593',
    'M 1170 632 L 1143 593',
    'M 1162 638 L 1133 599',
    'M 1158 651 L 1112 665',
    'M 1161 660 L 1116 676',
  ],
  circles: [
    [412, 650, 97],
    [412, 650, 69],
    [412, 650, 16],
    [1177, 650, 97],
    [1177, 650, 69],
    [1177, 650, 16],
  ],
  ground: 'M 71 749 L 1524 749',
};

const RIM_SPOKES = Array.from({ length: 5 }, (_, i) => {
  const angle = ((-90 + i * 72) * Math.PI) / 180;
  const spread = (7 * Math.PI) / 180;
  const getPoint = (r: number, a: number) =>
    `${Math.round(800 + r * Math.cos(a))} ${Math.round(470 + r * Math.sin(a))}`;
  return `M ${getPoint(62, angle - spread * 2)} L ${getPoint(205, angle - spread)} M ${getPoint(62, angle + spread * 2)} L ${getPoint(205, angle + spread)}`;
});

const RIM_NUTS: [number, number, number][] = Array.from({ length: 5 }, (_, i) => {
  const angle = ((-90 + 36 + i * 72) * Math.PI) / 180;
  return [Math.round(800 + 34 * Math.cos(angle)), Math.round(470 + 34 * Math.sin(angle)), 7];
});

const RIM: ShotOverlay = {
  outline: ['M 800 140 A 330 330 0 1 1 799.9 140 Z'],
  details: ['M 380 470 A 420 420 0 0 1 1220 470', ...RIM_SPOKES],
  circles: [[800, 470, 222], [800, 470, 210], [800, 470, 56], ...RIM_NUTS],
  ground: 'M 260 800 L 1340 800',
};

export const SHOT_GUIDES: Record<InspectionShotKey, ShotGuide> = {
  front: {
    overlay: FRONT,
    locator: { camera: [60, 4], target: [60, 30] },
  },
  rear: {
    overlay: REAR,
    locator: { camera: [60, 196], target: [60, 170] },
  },
  'side-left': {
    overlay: SIDE,
    locator: { camera: [4, 100], target: [32, 100] },
  },
  'side-right': {
    overlay: { ...SIDE, mirror: true },
    locator: { camera: [116, 100], target: [88, 100] },
  },
  'front-lower': {
    overlay: { ...FRONT, zoom: { scale: 1.5, from: [800, 610], to: [800, 470] } },
    locator: { camera: [60, 2], target: [60, 22] },
  },
  windshield: {
    overlay: {
      outline: [
        'M 300 720 L 520 200 Q 530 180 560 178 L 1040 178 Q 1070 180 1080 200 L 1300 720 Q 1300 740 1280 740 L 320 740 Q 300 740 300 720 Z',
      ],
      details: [
        'M 250 760 L 480 170 Q 492 140 530 138 L 1070 138 Q 1108 140 1120 170 L 1350 760',
        'M 760 200 H 840 Q 850 200 850 212 V 236 Q 850 248 838 248 H 762 Q 750 248 750 236 V 212 Q 750 200 760 200 Z',
        'M 800 178 V 200',
        'M 420 735 L 760 640',
        'M 860 735 L 1180 640',
      ],
      ground: 'M 200 800 Q 800 770 1400 800',
    },
    locator: { camera: [60, 6], target: [60, 62] },
  },
  'rim-front-left': { overlay: RIM, locator: { camera: [4, 52], target: [26, 52] } },
  'rim-rear-left': { overlay: RIM, locator: { camera: [4, 150], target: [26, 150] } },
  'rim-front-right': { overlay: RIM, locator: { camera: [116, 52], target: [94, 52] } },
  'rim-rear-right': { overlay: RIM, locator: { camera: [116, 150], target: [94, 150] } },
  'interior-driver': {
    overlay: {
      outline: [
        'M 1004 600 C 984 520 992 420 1018 330 C 1032 278 1040 238 1044 206 C 1046 186 1040 170 1044 150 C 1050 98 1070 66 1110 62 C 1150 60 1172 86 1170 128 C 1168 160 1160 178 1156 200 C 1150 236 1150 262 1148 300 C 1144 400 1130 500 1112 606 Z',
        'M 1012 598 C 960 590 860 586 760 588 C 726 588 704 576 696 596 C 688 616 700 650 724 664 C 760 684 900 690 1040 688 C 1100 686 1116 650 1110 612 Z',
        'M 513 237 A 92 178 14 1 0 427 583 A 92 178 14 1 0 513 237 Z',
      ],
      details: [
        'M 506 264 A 68 150 14 1 0 434 556 A 68 150 14 1 0 506 264 Z',
        'M 489 364 A 30 52 14 1 0 463 464 A 30 52 14 1 0 489 364 Z',
        'M 447 407 L 404 394',
        'M 505 421 L 536 426',
        'M 463 464 L 434 556',
        'M 452 452 L 330 500',
        'M 1022 580 C 1010 480 1020 390 1048 300 C 1060 262 1072 236 1080 214',
        'M 1086 112 C 1092 92 1116 90 1126 108 C 1132 122 1124 140 1108 142 C 1094 144 1082 130 1086 112 Z',
        'M 720 600 C 760 616 900 620 1000 612',
        'M 780 688 L 770 726',
        'M 1060 688 L 1070 726',
        'M 730 732 L 1110 732',
        'M 200 830 L 240 340 Q 300 150 480 108 L 1260 86 Q 1350 90 1370 170 L 1410 830',
        'M 250 330 Q 420 300 600 330 Q 640 338 650 372 L 652 470 Q 560 480 420 520 L 300 560',
        'M 1200 120 Q 1180 400 1100 700',
      ],
      ground: 'M 150 792 Q 800 770 1450 792',
    },
    locator: { camera: [6, 92], target: [44, 92] },
  },
  dashboard: {
    overlay: {
      outline: [
        'M 360 300 Q 800 180 1240 300 L 1200 560 Q 800 600 400 560 Z',
        'M 280 900 A 520 330 0 0 1 1320 900',
      ],
      details: [
        'M 720 360 H 880 V 480 H 720 Z',
        'M 600 420 L 540 330',
        'M 1000 420 L 1080 470',
        'M 495 495 A 130 130 0 1 1 705 495',
        'M 895 495 A 130 130 0 1 1 1105 495',
        'M 380 900 A 420 250 0 0 1 1220 900',
        'M 690 900 L 700 740 Q 800 700 900 740 L 910 900',
      ],
      circles: [
        [600, 420, 112],
        [1000, 420, 112],
      ],
    },
    locator: { camera: [44, 104], target: [44, 66] },
  },
  'rear-seats': {
    overlay: {
      outline: [
        'M 260 300 Q 260 240 320 236 L 1280 236 Q 1340 240 1340 300 L 1350 560 L 250 560 Z',
        'M 220 570 Q 220 556 240 556 L 1360 556 Q 1380 556 1380 570 L 1400 700 Q 1402 730 1370 732 L 230 732 Q 198 730 200 700 Z',
      ],
      details: [
        'M 620 250 L 615 555',
        'M 980 250 L 985 555',
        'M 380 120 H 520 Q 540 120 540 140 V 190 Q 540 210 520 210 H 380 Q 360 210 360 190 V 140 Q 360 120 380 120 Z',
        'M 740 130 H 860 Q 880 130 880 150 V 192 Q 880 210 860 210 H 740 Q 720 210 720 192 V 150 Q 720 130 740 130 Z',
        'M 1080 120 H 1220 Q 1240 120 1240 140 V 190 Q 1240 210 1220 210 H 1080 Q 1060 210 1060 190 V 140 Q 1060 120 1080 120 Z',
        'M 420 210 V 236 M 480 210 V 236 M 780 210 V 236 M 820 210 V 236 M 1120 210 V 236 M 1180 210 V 236',
        'M 600 556 H 640 V 580 H 600 Z',
        'M 960 556 H 1000 V 580 H 960 Z',
      ],
    },
    locator: { camera: [6, 132], target: [60, 132] },
  },
  trunk: {
    overlay: {
      outline: [
        'M 380 300 Q 380 270 410 268 L 1190 268 Q 1220 270 1220 300 L 1250 600 Q 1252 630 1222 632 L 378 632 Q 348 630 350 600 Z',
        'M 400 262 L 440 90 Q 446 70 470 70 L 1130 70 Q 1154 70 1160 90 L 1200 262',
        'M 330 650 L 1270 650 Q 1290 650 1290 670 L 1286 730 Q 1284 752 1262 752 L 338 752 Q 316 752 314 730 L 310 670 Q 310 650 330 650 Z',
      ],
      details: [
        'M 470 240 L 500 110 L 1100 110 L 1130 240 Z',
        'M 450 330 L 1150 330 L 1180 560 L 420 560 Z',
        'M 300 330 L 368 316 L 360 470 L 296 480 Z',
        'M 1300 330 L 1232 316 L 1240 470 L 1304 480 Z',
        'M 350 600 Q 300 600 290 640 L 280 760',
        'M 1250 600 Q 1300 600 1310 640 L 1320 760',
      ],
      ground: 'M 160 820 L 1440 820',
    },
    locator: { camera: [60, 198], target: [60, 172] },
  },
};

/** Neutral guide for shot keys added to the catalog without a drawing yet. */
export const FALLBACK_GUIDE: ShotGuide = {
  overlay: { outline: [], details: [] },
  locator: { camera: [60, 100], target: [60, 100] },
};

export function getShotGuide(shotKey: InspectionShotKey): ShotGuide {
  return SHOT_GUIDES[shotKey] ?? FALLBACK_GUIDE;
}

/** i18n key of a shot's label; the dashboard shot names the charge level for electric cars. */
export function getShotLabelKey(shotKey: InspectionShotKey, fuelType?: FuelType | null): string {
  if (shotKey === 'dashboard' && fuelType === FuelType.ELECTRIC) {
    return 'inspectionShots.dashboard-electric.label';
  }
  return `inspectionShots.${shotKey}.label`;
}

export function getShotHintKey(shotKey: InspectionShotKey): string {
  return `inspectionShots.${shotKey}.hint`;
}
